import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, ArrowRight, Play, RefreshCw, Grid, Droplets } from 'lucide-react';
import { useAcademyStore } from '../../store/academyStore';

type Level = {
    id: number;
    description: string;
    cssStart: string;
    cssEnd: string;
    targetStyle: React.CSSProperties; // Where the "weeds" or "dry spots" are
    waterStartStyle: React.CSSProperties; // Default water style before user edits
    hint: string;
    allowedProps: string[]; // Properties the user should use i.e 'grid-column-start'
};

const LEVELS: Level[] = [
    {
        id: 1,
        description: "Water the carrots! They are in the 3rd column. Use grid-column-start.",
        cssStart: "#water {",
        cssEnd: "}",
        targetStyle: { gridColumnStart: 3, gridRowStart: 1 },
        waterStartStyle: { gridRowStart: 1 },
        hint: "grid-column-start: 3;",
        allowedProps: ['grid-column-start']
    },
    {
        id: 2,
        description: "The weeds are taking over the bottom row (row 5). Water them out!",
        cssStart: "#water {",
        cssEnd: "}",
        targetStyle: { gridRowStart: 5, gridColumnStart: 1 },
        waterStartStyle: { gridColumnStart: 1 },
        hint: "grid-row-start: 5;",
        allowedProps: ['grid-row-start']
    },
    {
        id: 3,
        description: "Span the water across columns 2 to 4.",
        cssStart: "#water {",
        cssEnd: "}",
        targetStyle: { gridColumnStart: 2, gridColumnEnd: 5, gridRowStart: 1 },
        waterStartStyle: { gridRowStart: 1 },
        hint: "grid-column-start: 2; grid-column-end: 5;",
        allowedProps: ['grid-column-start', 'grid-column-end']
    },
    {
        id: 4,
        description: "Fill the entire last area: Row 2 to 6, Column 1 to 6. Use grid-area!",
        cssStart: "#water {",
        cssEnd: "}",
        targetStyle: { gridArea: '2 / 1 / 6 / 6' },
        waterStartStyle: {},
        hint: "grid-area: 2 / 1 / 6 / 6;",
        allowedProps: ['grid-area']
    }
];

export const GridGarden = () => {
    const { completeModule } = useAcademyStore();
    const [currentLevel, setCurrentLevel] = useState(0);
    const [userCode, setUserCode] = useState("");
    const [userStyle, setUserStyle] = useState<React.CSSProperties>({});
    const [isCorrect, setIsCorrect] = useState(false);

    const level = LEVELS[currentLevel];

    useEffect(() => {
        setIsCorrect(false);
        setUserCode("");
        setUserStyle(level.waterStartStyle);
    }, [currentLevel]);

    const handleCodeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const code = e.target.value;
        setUserCode(code);

        // Simple CSS parser for the game
        const lines = code.split(';').filter(l => l.trim());
        const newStyle: any = { ...level.waterStartStyle };

        lines.forEach(line => {
            const [prop, val] = line.split(':');
            if (prop && val) {
                const camelProp = prop.trim().replace(/-([a-z])/g, g => g[1].toUpperCase());
                // Basic security/relevance check: only allow grid props for now + values
                if (prop.includes('grid')) {
                    newStyle[camelProp] = val.trim();
                }
            }
        });
        setUserStyle(newStyle);

        // Check win condition
        checkWin(newStyle);
    };

    const checkWin = (style: any) => {
        // This is a naive check. A robust one compares computed styles or normalized logical values.
        // For this mini-game, we check if key properties match target values.
        let match = true;
        const target = level.targetStyle as any;

        for (const key in target) {
            // Convert numbers to strings for comparison (CSS values are usually strings)
            const targetVal = String(target[key]);
            const userVal = String(style[key]);

            // Handle shorthand expansion (too complex for naive, assume user must match shorthand if target uses it)
            // Or strict requirement: User code must essentially produce the grid placement.
            if (userVal !== targetVal) {
                // Fallback for number equality (3 vs "3")
                if (userVal != targetVal) match = false;
            }
        }

        // Special case for 'span' syntax vs Start/End numbers? 
        // For simplicity, we define Levels where specific answers are expected.

        if (match) {
            setIsCorrect(true);
            if (currentLevel === LEVELS.length - 1) {
                completeModule('css-grid', 100);
            }
        } else {
            setIsCorrect(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-white flex flex-col">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-green-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    <span className="text-slate-400 font-bold">Level {currentLevel + 1} of {LEVELS.length}</span>
                    <div className="flex gap-1">
                        {LEVELS.map((l, i) => (
                            <div
                                key={l.id}
                                className={`w-3 h-3 rounded-full ${i < currentLevel ? 'bg-green-500' : i === currentLevel ? 'bg-green-400 animate-pulse' : 'bg-slate-700'}`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex-1 flex flex-col lg:flex-row h-[calc(100vh-80px)]">
                {/* Editor (Left) */}
                <div className="w-full lg:w-1/3 bg-slate-800 p-8 flex flex-col border-r border-slate-700 shadow-xl z-10">
                    <div className="mb-6">
                        <h1 className="text-3xl font-black text-green-400 mb-2 font-comic tracking-wide">Grid Garden 🥕</h1>
                        <p className="text-slate-300 leading-relaxed text-lg">{level.description}</p>
                    </div>

                    <div className="flex-1 overflow-hidden flex flex-col">
                        <div className="bg-slate-900 rounded-t-xl p-2 px-4 border border-b-0 border-slate-700 flex items-center justify-between text-xs text-slate-500 font-bold uppercase tracking-wider">
                            <span>CSS Editor</span>
                            <span>style.css</span>
                        </div>
                        <div className="bg-slate-900 rounded-b-xl p-6 font-mono text-lg border border-slate-700 shadow-inner flex-1 overflow-auto relative leading-relaxed">
                            <div className="text-slate-500">
                                1  <span className="text-orange-400">#water</span> {'{'}
                            </div>
                            <div className="text-slate-500">
                                2    <span className="text-purple-400">display</span>: <span className="text-yellow-400">grid</span>;
                            </div>

                            <textarea
                                value={userCode}
                                onChange={handleCodeChange}
                                className="w-full bg-transparent outline-none text-white resize-none h-32 pl-4 focus:bg-white/5 rounded"
                                placeholder={`/* ${level.hint} */`}
                                spellCheck={false}
                            />

                            <div className="text-slate-500">
                                {'}'}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-between items-center">
                        <button
                            onClick={() => setUserCode("")}
                            className="text-slate-400 hover:text-white flex items-center gap-2 text-sm font-bold"
                        >
                            <RefreshCw size={14} /> Reset Code
                        </button>

                        <AnimatePresence>
                            {isCorrect && (
                                <motion.button
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    onClick={() => currentLevel < LEVELS.length - 1 && setCurrentLevel(c => c + 1)}
                                    className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold shadow-lg shadow-green-500/30 flex items-center gap-2"
                                >
                                    Next Level <ArrowRight size={18} />
                                </motion.button>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Visual Grid (Right) */}
                <div className="w-full lg:w-2/3 bg-[#8ba858] relative overflow-hidden flex items-center justify-center p-12">
                    <div className="w-full max-w-2xl aspect-square bg-[#8ba858] relative rounded-xl overflow-hidden shadow-2xl border-4 border-[#6e8a40]">

                        {/* Background Grid (Soil) */}
                        <div className="absolute inset-0 grid grid-cols-5 grid-rows-5 gap-2 p-2">
                            {[...Array(25)].map((_, i) => (
                                <div key={i} className="bg-[#789445] rounded-full opacity-50 relative">
                                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/20 text-xs font-bold">{i + 1}</span>
                                </div>
                            ))}
                        </div>

                        {/* Target Area (Carrots/Dirt) */}
                        <div className="absolute inset-0 grid grid-cols-5 grid-rows-5 gap-2 p-2 pointer-events-none">
                            <div
                                style={level.targetStyle}
                                className="bg-[#6b5b45]/30 rounded-xl relative flex items-center justify-center"
                            >
                                <span className="text-4xl filter drop-shadow-md">🥕</span>
                            </div>
                        </div>

                        {/* Water Overlay (The Player) */}
                        <div className="absolute inset-0 grid grid-cols-5 grid-rows-5 gap-2 p-2 pointer-events-none transition-all duration-500 ease-in-out">
                            <div
                                style={userStyle}
                                className="bg-blue-400/50 rounded-xl border-4 border-blue-400 relative overflow-hidden"
                            >
                                {isCorrect && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="absolute inset-0 flex items-center justify-center bg-blue-500/20"
                                    >
                                        <Droplets className="text-blue-100 w-12 h-12 animate-bounce" />
                                    </motion.div>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* Tooltip */}
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                        <Grid size={16} /> Grid: 5 columns x 5 rows
                    </div>
                </div>
            </div>
        </div>
    );
};
